/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {InvoiceUploadStats} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../util/utils";
import Account from "../../../models/accountModel";
import UploadStats from "./uploadStats";

interface Props {
    stats: InvoiceUploadStats;
    loading: boolean;
}

export default function UploadStatsController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {stats} = props;
    const [loading, setLoading] = useState(props.loading);
    const proactiveEnabled = account?.settings.proactivelyUploadInvoices ?? false;
    const autoRespondEnabled = account?.settings.autoRespondInvoiceRequests ?? false;

    function onChangeProactiveUpload(value: boolean) {
        if (!account) return;
        setLoading(true);
        account.settings.proactivelyUploadInvoices = value;
        updateAccount(account, (data: ResponseData) => {
            if (data.success) {
                const label = value ? 'ingeschakeld' : 'uitgeschakeld';
                showSnackbar(`Het proactief uploaden van de facturen is ${label}`);
            } else {
                account.settings.proactivelyUploadInvoices = !value;
                if (data.handled) return;
                const label = value ? 'inschakelen' : 'uitschakelen'
                showDialog(createErrorMessage(data.message ??
                        `Het ${label} van het proactief uploaden is niet gelukt.`),
                    `Proactief uploaded ${label} mislukt`);
            }
        })
    }

    function onChangeAutoRespond(value: boolean) {
        if (!account) return;
        setLoading(true);
        account.settings.autoRespondInvoiceRequests = value;
        updateAccount(account, (data: ResponseData) => {
            if (data.success) {
                const label = value ? 'ingeschakeld' : 'uitgeschakeld';
                showSnackbar(`Het automatisch beantwoorden van de aanvragen is ${label}`);
            } else {
                account.settings.autoRespondInvoiceRequests = !value;
                if (data.handled) return;
                const label = value ? 'inschakelen' : 'uitschakelen'
                showDialog(createErrorMessage(data.message ??
                        `Het ${label} van het automatisch beantwoorden is niet gelukt.`),
                    `Automatisch beantwoorden ${label} mislukt`);
            }
        })
    }

    function updateAccount(account: Account, callback: (data: ResponseData) => void) {
        setLoading(true);
        account.update(undefined, undefined, undefined,
            (account: Account, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) context.onChangeContext({account});
                callback(data);
            });
    }

    useEffect(() => {
        setLoading(props.loading);
    }, [props.loading]);

    return (
        <UploadStats onChangeProactiveUpload={onChangeProactiveUpload} onChangeAutoRespond={onChangeAutoRespond}
                     proactiveEnabled={proactiveEnabled} stats={stats} loading={loading}
                     autoRespondEnabled={autoRespondEnabled}/>
    );
}
